import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, FileVideo, FileText, Video, Link2, Unlink, Loader2 } from "lucide-react";
import { MeetingCard, RecordingCard } from "./CalendarCards";
import { MeetingBriefCard } from "./MeetingBriefCard";

const EmptyState = ({ icon: Icon, iconClass, boxClass, title, hint, children, testId }) => (
  <motion.div className="text-center py-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={testId}>
    <div className={`p-6 rounded-2xl border border-white/10 inline-block mb-4 ${boxClass}`}>
      <Icon className={`h-12 w-12 ${iconClass}`} />
    </div>
    <p className="text-gray-400 mb-3">{title}</p>
    <p className="text-gray-600 text-xs mb-4">{hint}</p>
    {children}
  </motion.div>
);

export const CalendarLoading = () => (
  <div className="text-center py-12" data-testid="calendar-loading">
    <motion.div className="w-12 h-12 border-2 border-ism-teal/30 border-t-ism-teal rounded-full mx-auto mb-4" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
    <p className="text-gray-500 text-sm">Loading calendar...</p>
  </div>
);

export const UpcomingPanel = ({ meetings, outlookConnected, busy, onJoin, onAddAttendee, onConnect, onCreateMeeting }) => {
  if (meetings.length > 0) {
    return (
      <div className="space-y-3" data-testid="upcoming-meetings">
        {meetings.map((m, idx) => <MeetingCard key={m.id || idx} meeting={m} index={idx} onJoin={onJoin} onAddAttendee={onAddAttendee} />)}
      </div>
    );
  }
  return (
    <EmptyState icon={Calendar} iconClass="text-ism-teal" boxClass="bg-gradient-to-br from-ism-teal/10 to-ism-teal/10" title="No upcoming meetings" testId="upcoming-empty"
      hint={outlookConnected ? "Your Outlook is connected. Meetings will appear here." : "Connect your Outlook calendar to see meetings."}>
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button onClick={(e) => { e.stopPropagation(); outlookConnected ? onCreateMeeting() : onConnect(); }} disabled={busy} size="sm" className="bg-gradient-to-r from-ism-teal to-ism-teal hover:from-ism-teal hover:to-ism-teal-light text-white rounded-lg" data-testid="upcoming-empty-cta">
          {outlookConnected ? <><Video className="h-4 w-4 mr-2" /> Create Zoom Meeting</> : <><Link2 className="h-4 w-4 mr-2" /> Connect Outlook</>}
        </Button>
      </motion.div>
    </EmptyState>
  );
};

export const RecordingsPanel = ({ recordings, onView }) => {
  if (recordings.length > 0) {
    return (
      <div className="space-y-3" data-testid="recordings-list">
        {recordings.map((r, idx) => <RecordingCard key={r.uuid || idx} recording={r} index={idx} onView={onView} />)}
      </div>
    );
  }
  return <EmptyState icon={FileVideo} iconClass="text-ism-teal" boxClass="bg-gradient-to-br from-ism-teal/10 to-ism-teal/10" title="No recordings available" hint="Meeting recordings will appear here after your Zoom calls." testId="recordings-empty" />;
};

export const BriefsPanel = ({ briefs, loading, outlookConnected, expandedId, onToggle, onConnect }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="briefs-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
        <p className="text-gray-500 text-sm ml-3">Generating meeting briefs...</p>
      </div>
    );
  }
  if (briefs.length > 0) {
    return (
      <div className="space-y-4" data-testid="briefs-list">
        {briefs.map((b, idx) => (
          <MeetingBriefCard key={b.meeting_id || idx} brief={b} index={idx} isExpanded={expandedId === b.meeting_id} onToggle={() => onToggle(b.meeting_id)} />
        ))}
      </div>
    );
  }
  return (
    <EmptyState icon={FileText} iconClass="text-amber-400" boxClass="bg-gradient-to-br from-amber-500/10 to-orange-500/10" title="No meeting briefs available" testId="briefs-empty"
      hint={outlookConnected ? "Briefs will appear when you have upcoming meetings." : "Connect your calendar to generate meeting briefs."}>
      {!outlookConnected && (
        <Button onClick={(e) => { e.stopPropagation(); onConnect(); }} size="sm" className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white rounded-lg" data-testid="briefs-connect-btn">
          <Link2 className="h-4 w-4 mr-2" /> Connect Calendar
        </Button>
      )}
    </EmptyState>
  );
};

const IntegrationRow = ({ icon, title, subtitle, iconBox = "bg-ism-teal/10 border-ism-teal/20", dim, children, testId }) => (
  <div className={`glass-card-light rounded-xl p-5 ${dim ? "opacity-50" : ""}`} data-testid={testId}>
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl border ${iconBox}`}>{icon}</div>
        <div>
          <h3 className="text-white text-sm">{title}</h3>
          <p className="text-gray-500 text-xs">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  </div>
);

const OutlookIcon = () => (
  <svg className="h-6 w-6 text-ism-teal" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

export const ConnectPanel = ({ outlookStatus, integrationStatus, connecting, onConnect, onDisconnect }) => {
  const zoomOn = !!integrationStatus?.zoom?.configured;
  return (
    <motion.div className="space-y-4 py-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="connect-panel">
      <IntegrationRow icon={<OutlookIcon />} title="Microsoft Outlook" testId="integration-outlook"
        subtitle={outlookStatus?.connected ? `Connected: ${outlookStatus.outlook_email}` : "Sync your Outlook calendar events"}>
        {outlookStatus?.connected ? (
          <Button onClick={(e) => { e.stopPropagation(); onDisconnect(); }} size="sm" className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg" data-testid="disconnect-outlook-btn">
            <Unlink className="h-4 w-4 mr-1" /> Disconnect
          </Button>
        ) : (
          <Button onClick={(e) => { e.stopPropagation(); onConnect(); }} disabled={connecting} size="sm" className="bg-ism-teal/20 hover:bg-ism-teal/30 text-ism-teal border border-ism-teal/30 rounded-lg" data-testid="connect-outlook-btn">
            {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-4 w-4 mr-1" />} Connect
          </Button>
        )}
      </IntegrationRow>

      <IntegrationRow icon={<Video className="h-6 w-6 text-ism-teal" />} title="Zoom" subtitle={zoomOn ? "Connected - Server to Server OAuth" : "Not configured"} testId="integration-zoom">
        <Badge className={zoomOn ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-gray-500/20 text-gray-400 border-gray-500/30"}>{zoomOn ? "Active" : "Inactive"}</Badge>
      </IntegrationRow>

      <IntegrationRow icon={<Calendar className="h-6 w-6 text-red-400" />} iconBox="bg-red-500/10 border-red-500/20" title="Google Calendar" subtitle="Coming soon" dim testId="integration-google">
        <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Coming Soon</Badge>
      </IntegrationRow>
    </motion.div>
  );
};
